import { Badge, LinearProgress, Tab, Tabs } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useEmrSettings } from '../hooks/useEmrSettings'
import { EmrReportTab } from './EmrReportTab'

export const EMRSections = forwardRef(
  (
    {
      mentorProfileData,
      emrSectionsApiSignature = '/api/secure/practitioner/clinical/emr-settings/emr-sections',
      emrSettingsSaveApiSignature = '/api/secure/practitioner/emr-settings/emr-sections/save',
      hideTabs = false,
      hideSaveButton,
      hideReportsTabsRelatedActions = false,
      hideEmrSectionHeader = false,
      initalTab,
      hideReordering = false,
      hidePrintOnlyEditorOnlyToggles = false,
      /** When set, show only this report type (filter from emr-report-types). Used for page-specific Settings shortcut. */
      singleReportTypeId,
    },
    componentRef,
  ) => {
    const {
      emrSettingsMap,
      loading,
      saveLoading,
      fetchEmrSettings,
      saveEmrSettings,
      toggleSectionForReportType,
      togglePrintableForReportType,
      updateSectionPrintOnlyEditorOnlyForReportType,
      reorderSectionsForReportType,
      updateFieldForReportType,
      metaEmrReportTypes,
      categories,
      emrReportTypes,
      TAB_OPTIONS,
      sectionSubsectionsMap,
      expandedSections,
      toggleSectionExpansion,
      toggleSubsectionExclusion,
      toggleSubsectionPrintable,
      fetchSectionEffectiveLayout,
    } = useEmrSettings({
      fetchApiSignature: emrSectionsApiSignature,
      saveApiSignature: emrSettingsSaveApiSignature,
      approvedServicesMap: mentorProfileData?.teacherUnEditableFields?.servicesApproved,
      fetchCategories: !hideReportsTabsRelatedActions, // Only fetch categories if the form that uses them is visible
    })

    // When singleReportTypeId is set, show only that report type (from /api/health/emr-report-types); use meta so it works even if not in approved list
    const displayReportTypes = useMemo(() => {
      if (!singleReportTypeId) return emrReportTypes
      const raw = metaEmrReportTypes || []
      const found = raw.find((rt) => rt?.id === singleReportTypeId)
      if (found) return [{ id: found.id, title: found.title || found.id, reportType: found.id }]
      const fromFiltered = emrReportTypes.find((rt) => rt.id === singleReportTypeId)
      return fromFiltered ? [fromFiltered] : []
    }, [emrReportTypes, metaEmrReportTypes, singleReportTypeId])

    const [activeTab, setActiveTab] = useState(initalTab || false)

    // Update active tab when emrReportTypes loads
    useEffect(() => {
      if (displayReportTypes.length > 0) {
        // If activeTab is not set or doesn't match any report type, set it to the first one
        if (!activeTab || !displayReportTypes.find((rt) => rt.id === activeTab)) {
          setActiveTab(displayReportTypes[0].id)
        }
      }
    }, [displayReportTypes, activeTab])

    const handleSaveAllSettings = async () => {
      const result = await saveEmrSettings()
      if (result) {
        console.log('EMR settings saved successfully')
      } else {
        console.error('Failed to save EMR settings')
      }
      return result
    }

    useImperativeHandle(componentRef, () => ({
      save: handleSaveAllSettings,
    }))

    useEffect(() => {
      fetchEmrSettings()
    }, [fetchEmrSettings])

    useEffect(() => {
      if (
        !!activeTab &&
        displayReportTypes.length > 0 &&
        displayReportTypes.find((rt) => rt.id === activeTab)
      ) {
        return
      }
      if (displayReportTypes.length > 0) {
        setActiveTab(displayReportTypes[0].id)
      }
    }, [displayReportTypes, activeTab])

    return (
      <div style={{ width: '100%', paddingBottom: '80px' }}>
        {loading && <LinearProgress />}

        <div className="d-flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {!hideTabs && (
            <>
              {!!displayReportTypes && displayReportTypes.length > 0 && activeTab ? (
                <div>
                  <Tabs
                    value={activeTab}
                    onChange={(event, newValue) => setActiveTab(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    {displayReportTypes.map((reportType) => (
                      <Tab
                        key={reportType.id}
                        value={reportType.id}
                        label={
                          <span>
                            {reportType.title}
                            <Badge
                              badgeContent={
                                emrSettingsMap?.[reportType.id]?.emrSections?.length || 0
                              }
                              color="secondary"
                              sx={{ ml: 2 }}
                            />
                          </span>
                        }
                      />
                    ))}
                  </Tabs>
                </div>
              ) : (
                <div className="text-center text-muted py-3">Loading report types...</div>
              )}
            </>
          )}
          {!loading && !hideSaveButton && (
            <div style={{ marginLeft: 'auto', marginTop: '0px', marginBottom: '26px' }}>
              <button
                className="btn btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(67, 56, 202, 0.4)',
                  transition: 'all 0.3s ease',
                  minWidth: '180px',
                  cursor: saveLoading ? 'not-allowed' : 'pointer',
                  opacity: saveLoading ? 0.7 : 1,
                }}
                onClick={handleSaveAllSettings}
                disabled={saveLoading}
                onMouseEnter={(e) => {
                  if (!saveLoading) {
                    e.target.style.background = 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)'
                    e.target.style.boxShadow = '0 6px 20px rgba(67, 56, 202, 0.5)'
                    e.target.style.transform = 'translateY(-2px)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saveLoading) {
                    e.target.style.background = 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
                    e.target.style.boxShadow = '0 4px 14px rgba(67, 56, 202, 0.4)'
                    e.target.style.transform = 'translateY(0)'
                  }
                }}
              >
                Save All Settings{' '}
                {saveLoading && <Spinner animation="border" variant="light" size="sm" style={{ marginLeft: '8px' }} />}
              </button>
            </div>
          )}
        </div>

        {displayReportTypes.length > 0 && (
          <div
            className="tab-content"
            style={{
              border: '1px solid #dee2e6',
              borderTop: 'none',
              borderRadius: '0 0 0.375rem 0.375rem',
              padding: '1rem',
            }}
          >
            <EmrReportTab
              emrEditorProperties={emrSettingsMap?.[activeTab]}
              onSectionToggle={(sectionItem, checked) =>
                toggleSectionForReportType(activeTab, sectionItem, checked)
              }
              hideReportsTabsRelatedActions={hideReportsTabsRelatedActions}
              hideEmrSectionHeader={hideEmrSectionHeader}
              hidePrintOnlyEditorOnlyToggles={hidePrintOnlyEditorOnlyToggles}
              onPrintableToggle={(sectionItem) =>
                togglePrintableForReportType(activeTab, sectionItem)
              }
              onSectionPrintOnlyEditorOnlyChange={(sectionItem, { printOnly, editorOnly }) =>
                updateSectionPrintOnlyEditorOnlyForReportType(activeTab, sectionItem, { printOnly, editorOnly })
              }
              onReorder={(dragResult) => reorderSectionsForReportType(activeTab, dragResult)}
              onFieldUpdate={updateFieldForReportType}
              reportType={activeTab}
              hideReordering={hideReordering}
              metaEmrReportTypes={metaEmrReportTypes}
              categories={categories}
              tabOptions={TAB_OPTIONS}
              sectionSubsectionsMap={sectionSubsectionsMap}
              expandedSections={expandedSections}
              toggleSectionExpansion={toggleSectionExpansion}
              toggleSubsectionExclusion={toggleSubsectionExclusion}
              toggleSubsectionPrintable={toggleSubsectionPrintable}
              fetchSectionEffectiveLayout={fetchSectionEffectiveLayout}
              loading={loading}
            />
          </div>
        )}
      </div>
    )
  },
)
